import { useState } from "react";
import { Popover } from "radix-ui";
import { Permission, hasPermission, type CommunityStructure, type OnboardingConfig } from "@atrium/shared";
import { api, errorMessage } from "../../../lib/api";
import { cn } from "../../../lib/cn";
import { Button, IconButton } from "../../../components/ui/Button";
import { Switch } from "../../../components/ui/Controls";
import { Input, Textarea } from "../../../components/ui/Input";
import { Select, SettingsGroup, SettingsRow, UnsavedBar } from "../../../components/ui/Settings";
import { toast } from "../../../components/ui/Toast";
import { IconArrowUp, IconEye, IconPlus, IconShield, IconTrash } from "../../../components/icons";
import { RoleChecklist } from "../ChannelDialogs";
import { channelIcon } from "../ChannelSidebar";
import { OnboardingFlow } from "../../onboarding/OnboardingFlow";
import { useDraft, useRefreshCommunity, type SectionProps } from "./shared";

type Question = NonNullable<OnboardingConfig["questions"]>[number];
type Option = Question["options"][number];

const newId = () => Math.random().toString(36).slice(2, 10);
const LOBBY_MAX = 10;
const QUESTION_MAX = 10;
const OPTION_MAX = 20;

/** Every field filled in, so drafts compare cleanly against the server copy. */
function normalise(o: OnboardingConfig) {
  return {
    enabled: o.enabled ?? false,
    welcomeTitle: o.welcomeTitle ?? "",
    welcomeBody: o.welcomeBody ?? "",
    requireRules: o.requireRules ?? false,
    introChannelId: o.introChannelId ?? null,
    lobbyChannelIds: o.lobbyChannelIds ?? [],
    questions: (o.questions ?? []).map((q) => ({
      id: q.id,
      title: q.title,
      multiple: q.multiple ?? false,
      required: q.required ?? false,
      options: q.options.map((op) => ({ id: op.id, label: op.label, emoji: op.emoji ?? "", roleIds: op.roleIds ?? [] })),
    })),
  };
}
type Draft = ReturnType<typeof normalise>;

function move<T>(list: T[], from: number, to: number) {
  const next = [...list];
  const [it] = next.splice(from, 1);
  next.splice(to, 0, it);
  return next;
}

export function OnboardingSection({ structure, go }: SectionProps) {
  const c = structure.community;
  const refresh = useRefreshCommunity(c.id);
  const d = useDraft<Draft>(normalise(c.onboarding));
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const draft = d.draft;
  const canRoles = c.isOwner || hasPermission(c.myPermissions, Permission.MANAGE_ROLES) || hasPermission(c.myPermissions, Permission.ADMINISTRATOR);
  // Roles onboarding can hand out: never @everyone, app-managed roles or Administrator.
  const roles = structure.roles.filter((r) => !r.isDefault && !r.managed && !hasPermission(r.permissions, Permission.ADMINISTRATOR));
  const textChannels = structure.channels.filter((ch) => ch.type !== "voice" && ch.type !== "stage");

  const setQuestions = (questions: Draft["questions"]) => d.set("questions", questions);
  const setQuestion = (i: number, patch: Partial<Draft["questions"][number]>) => setQuestions(draft.questions.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  const setOption = (qi: number, oi: number, patch: Partial<Draft["questions"][number]["options"][number]>) =>
    setQuestion(qi, { options: draft.questions[qi].options.map((o, j) => (j === oi ? { ...o, ...patch } : o)) });

  const toSave = (): Draft => ({
    ...draft,
    welcomeTitle: draft.welcomeTitle.trim(),
    welcomeBody: draft.welcomeBody.trim(),
    questions: draft.questions.map((q) => ({
      ...q,
      title: q.title.trim(),
      options: q.options.filter((o) => o.label.trim()).map((o) => ({ ...o, label: o.label.trim(), emoji: o.emoji.trim() })),
    })),
  });
  const asConfig = (x: Draft): OnboardingConfig => ({
    ...x,
    questions: x.questions.map((q) => ({ ...q, options: q.options.map((o) => ({ ...o, emoji: o.emoji || undefined })) })),
  });

  const save = async () => {
    const next = toSave();
    const bad = next.questions.find((q) => !q.title || q.options.length === 0);
    if (bad) return toast.error(bad.title ? `Add at least one option to “${bad.title}”` : "Every question needs a title");
    setSaving(true);
    try {
      await api.put(`/api/communities/${c.id}/onboarding`, asConfig(next));
      await refresh();
      toast.show("Saved");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleLobby = (id: string) =>
    d.set("lobbyChannelIds", draft.lobbyChannelIds.includes(id) ? draft.lobbyChannelIds.filter((x) => x !== id) : [...draft.lobbyChannelIds, id].slice(0, LOBBY_MAX));

  return (
    <div>
      <SettingsGroup
        action={
          <Button size="sm" variant="ghost" icon={IconEye} onClick={() => setPreview(true)}>
            Preview
          </Button>
        }
        title="Onboarding"
        description="New members go through a short welcome before joining in."
      >
        <SettingsRow title="Enabled" htmlFor="ob-enabled">
          <Switch id="ob-enabled" label="Enable onboarding" checked={draft.enabled} onCheckedChange={(v) => d.set("enabled", v)} />
        </SettingsRow>
      </SettingsGroup>

      <SettingsGroup title="Welcome">
        <div className="space-y-3 px-4 py-3">
          <Input value={draft.welcomeTitle} onChange={(e) => d.set("welcomeTitle", e.target.value)} maxLength={100} placeholder={`Welcome to ${c.name}`} aria-label="Welcome title" />
          <Textarea value={draft.welcomeBody} onChange={(e) => d.set("welcomeBody", e.target.value)} maxLength={2000} rows={3} placeholder="A line or two about the community" aria-label="Welcome message" />
        </div>
      </SettingsGroup>

      <SettingsGroup title="Rules and channels">
        <SettingsRow
          title="Accept the rules"
          description={
            c.rules ? (
              "Members agree to the community rules before continuing."
            ) : (
              <>
                Write rules in{" "}
                <button className="underline hover:text-fg" onClick={() => go("access")}>
                  Access
                </button>{" "}
                first.
              </>
            )
          }
          htmlFor="ob-rules"
          disabled={!c.rules}
        >
          <Switch id="ob-rules" label="Require accepting the rules" checked={draft.requireRules && !!c.rules} disabled={!c.rules} onCheckedChange={(v) => d.set("requireRules", v)} />
        </SettingsRow>
        <SettingsRow title="Intro channel" description="Where members land when they finish.">
          <Select
            aria-label="Intro channel"
            value={draft.introChannelId ?? ""}
            onChange={(v) => d.set("introChannelId", v || null)}
            options={[{ value: "", label: "Landing channel" }, ...textChannels.map((ch) => ({ value: ch.id, label: `#${ch.name}` }))]}
          />
        </SettingsRow>
        <SettingsRow
          title="Lobby"
          description={
            draft.lobbyChannelIds.length
              ? "Until they finish, new members only see these channels."
              : "Pick channels to hold new members in until they finish. Without any, they see everything straight away."
          }
          stack
        >
          <div role="group" aria-label="Lobby channels" className="flex flex-wrap gap-1.5">
            {structure.channels.map((ch) => {
              const on = draft.lobbyChannelIds.includes(ch.id);
              const Icon = channelIcon(ch);
              return (
                <button
                  key={ch.id}
                  type="button"
                  aria-pressed={on}
                  disabled={!on && draft.lobbyChannelIds.length >= LOBBY_MAX}
                  onClick={() => toggleLobby(ch.id)}
                  className={cn(
                    "inline-flex h-7 items-center gap-1 rounded-full border px-2.5 text-sm disabled:opacity-40",
                    on ? "border-accent bg-accent-soft text-fg" : "border-line-strong text-fg-2 hover:bg-surface hover:text-fg",
                  )}
                >
                  <Icon className="icon size-4" aria-hidden />
                  {ch.name}
                </button>
              );
            })}
          </div>
        </SettingsRow>
      </SettingsGroup>

      <div className="mb-2 flex items-end justify-between gap-4 px-0.5">
        <div>
          <h3 className="text-sm font-semibold text-fg-2">Questions</h3>
          <p className="mt-0.5 text-xs text-fg-3">Answers can give members roles.{!canRoles && " You need Manage roles to add roles to answers."}</p>
        </div>
      </div>
      {draft.questions.map((q, qi) => (
        <SettingsGroup
          key={q.id}
          action={
            <div className="flex">
              <IconButton size="sm" label="Move up" icon={IconArrowUp} disabled={qi === 0} onClick={() => setQuestions(move(draft.questions, qi, qi - 1))} />
              <IconButton size="sm" label="Move down" icon={IconArrowUp} className="[&_svg]:rotate-180" disabled={qi === draft.questions.length - 1} onClick={() => setQuestions(move(draft.questions, qi, qi + 1))} />
              <IconButton size="sm" label="Remove question" icon={IconTrash} tone="danger" onClick={() => setQuestions(draft.questions.filter((_, j) => j !== qi))} />
            </div>
          }
          title={`Question ${qi + 1}`}
        >
          <div className="px-4 py-3">
            <Input value={q.title} onChange={(e) => setQuestion(qi, { title: e.target.value })} maxLength={100} placeholder="What brings you here?" aria-label={`Question ${qi + 1}`} autoFocus={!q.title} />
          </div>
          <SettingsRow title="Several answers" htmlFor={`ob-multi-${q.id}`}>
            <Switch id={`ob-multi-${q.id}`} label="Allow several answers" checked={q.multiple} onCheckedChange={(v) => setQuestion(qi, { multiple: v })} />
          </SettingsRow>
          <SettingsRow title="Required" htmlFor={`ob-req-${q.id}`}>
            <Switch id={`ob-req-${q.id}`} label="Required" checked={q.required} onCheckedChange={(v) => setQuestion(qi, { required: v })} />
          </SettingsRow>
          {q.options.map((o, oi) => (
            <div key={o.id} className="flex items-center gap-2 px-4 py-2">
              <Input value={o.emoji} onChange={(e) => setOption(qi, oi, { emoji: e.target.value })} maxLength={16} placeholder="🙂" aria-label="Option emoji" className="!h-8 !w-12 shrink-0 !px-1 text-center" />
              <Input value={o.label} onChange={(e) => setOption(qi, oi, { label: e.target.value })} maxLength={60} placeholder="Answer" aria-label="Answer" className="!h-8 min-w-0 flex-1" autoFocus={!o.label && oi > 0} />
              <OptionRoles roles={roles} option={o} disabled={!canRoles} onChange={(roleIds) => setOption(qi, oi, { roleIds })} />
              <IconButton
                size="sm"
                label="Remove answer"
                icon={IconTrash}
                tone="danger"
                disabled={q.options.length <= 1}
                onClick={() => setQuestion(qi, { options: q.options.filter((_, j) => j !== oi) })}
              />
            </div>
          ))}
          <div className="px-2 py-1.5">
            <Button
              size="sm"
              variant="ghost"
              icon={IconPlus}
              disabled={q.options.length >= OPTION_MAX}
              onClick={() => setQuestion(qi, { options: [...q.options, { id: newId(), label: "", emoji: "", roleIds: [] }] })}
            >
              Add answer
            </Button>
          </div>
        </SettingsGroup>
      ))}
      <Button
        icon={IconPlus}
        disabled={draft.questions.length >= QUESTION_MAX}
        onClick={() => setQuestions([...draft.questions, { id: newId(), title: "", multiple: false, required: false, options: [{ id: newId(), label: "", emoji: "", roleIds: [] }] }])}
      >
        Add question
      </Button>

      <UnsavedBar show={d.dirty} saving={saving} onReset={d.reset} onSave={() => void save()} />
      <OnboardingFlow structure={structure} open={preview} onOpenChange={setPreview} preview config={asConfig(toSave())} />
    </div>
  );
}

function OptionRoles({
  roles,
  option,
  disabled,
  onChange,
}: {
  roles: CommunityStructure["roles"];
  option: Pick<Option, "roleIds">;
  disabled: boolean;
  onChange: (ids: string[]) => void;
}) {
  const chosen = roles.filter((r) => option.roleIds.includes(r.id));
  const label = chosen.length ? chosen.map((r) => r.name).join(", ") : "Roles";
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <Button size="sm" variant="ghost" icon={IconShield} disabled={disabled} className="max-w-40" aria-label={chosen.length ? `Roles: ${label}` : "Give roles"}>
          <span className={cn("truncate", !chosen.length && "text-fg-3")}>{label}</span>
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="end" sideOffset={6} collisionPadding={8} className="z-[60] w-64 rounded-lg bg-surface p-2 shadow-pop animate-pop-in">
          <RoleChecklist roles={roles} value={option.roleIds} onChange={(v) => onChange(v.slice(0, 10))} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
